import React from 'react';
import { Paths } from './constants';
import { RouteObject } from 'react-router';
import {
    ProtectedRouteLayout,
    CloudAuthProviderLayout,
    PublicRouteLayout,
    AuthRouteLayout,
    RestrictedRouteLayout,
} from '@/layout';
import { Loading } from '@/components/loading';
import { JSXElement } from '@/common/types';
import { ErrorPage } from '@/components/error-boundary/error-page';
import { env } from '@/utils/shared/env';
import { Navigate } from 'react-router';
import { SignIntoDevRealm } from '@/components/authentication-guard';

const loadRouteLazy = (
    importFunction: () => Promise<{ [key: string]: React.ComponentType }>,
    componentName: string,
) => {
    return async (): Promise<{ element: JSXElement }> => {
        const module = await importFunction();
        const Component = module[componentName];
        return {
            element: <Component />,
        };
    };
};

const authRoutes: RouteObject[] =
    env.VITE_MODE === 'local'
        ? [
              {
                  element: <AuthRouteLayout />,
                  children: [{ path: Paths.LOCAL_LOGIN, element: <SignIntoDevRealm /> }],
              },
          ]
        : [];

const publicRoutes: RouteObject[] =
    env.VITE_MODE === 'local'
        ? [
              {
                  element: <PublicRouteLayout />,
                  children: [{ path: '/', element: <Navigate to={Paths.BASE} replace /> }],
              },
          ]
        : [];

const protectedRoutes: RouteObject[] = [
    {
        element: <ProtectedRouteLayout />,
        children: [
            {
                path: Paths.BASE,
                lazy: loadRouteLazy(() => import('@/routes/base/jobs-and-tasks'), 'JobsAndTasksRoute'),
            },
            {
                path: Paths.JOBS,
                children: [
                    {
                        path: ':id',
                        lazy: loadRouteLazy(() => import('@/routes/jobs/job-history-id'), 'JobDetailsRoute'),
                    },
                ],
            },
            {
                path: Paths.TASKS,
                children: [
                    {
                        path: ':id',
                        lazy: loadRouteLazy(
                            () => import('@/routes/tasks/task-history-id'),
                            'TaskDetailsRoute',
                        ),
                    },
                ],
            },
        ],
    },
];

const restrictedRoutes: RouteObject[] = [
    {
        element: <RestrictedRouteLayout />,
        children: [
            {
                path: Paths.RESTRICTED,
                lazy: loadRouteLazy(() => import('@/routes/no-entitlement/no-entitlement'), 'NoEntitlement'),
            },
            {
                path: Paths.NO_PERMISSIONS,
                lazy: loadRouteLazy(() => import('@/routes/no-permissions/no-permissions'), 'NoPermissions'),
            },
        ],
    },
];

export const routes: RouteObject[] = [
    {
        element: <CloudAuthProviderLayout />,
        hydrateFallbackElement: <Loading />,
        errorElement: <ErrorPage />,
        children: [
            ...authRoutes,
            ...publicRoutes,
            ...protectedRoutes,
            ...restrictedRoutes,
            {
                path: Paths.ALL,
                lazy: loadRouteLazy(() => import('@/routes/not-found/not-found'), 'NotFound'),
            },
        ],
    },
];
